import { NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { hasModule } from "@/lib/permissions";
import {
  LayoutDashboard, Users, CalendarDays, ClipboardList, Wallet,
  Briefcase, Smartphone, Receipt, TrendingUp, LogOut, Bell, Search,
  Network, Target, UserPlus, UserMinus, PiggyBank, FileSpreadsheet, BadgeIndianRupee,
  MapPin, Fingerprint, ShieldAlert, HardHat, MapPinned, Car, FileCheck2, UserCog, Sparkles, Menu, X,
} from "lucide-react";

const NAV_GROUPS = [
  {
    label: "Workforce",
    items: [
      { to: "/", label: "Dashboard", icon: LayoutDashboard, module: "dashboard" },
      { to: "/employees", label: "Employees", icon: Users, module: "employees" },
      { to: "/workers", label: "Workers", icon: HardHat, module: "workers" },
      { to: "/field-staff", label: "Field Staff", icon: MapPinned, module: "field_staff" },
      { to: "/org-chart", label: "Org Chart", icon: Network, module: "org_chart" },
    ],
  },
  {
    label: "Operations",
    items: [
      { to: "/attendance", label: "Attendance", icon: CalendarDays, module: "attendance" },
      { to: "/punch", label: "Punch In/Out", icon: Fingerprint, module: "punch" },
      { to: "/locations", label: "Geofences", icon: MapPin, module: "locations" },
      { to: "/leaves", label: "Leaves", icon: ClipboardList, module: "leaves" },
      { to: "/performance", label: "Performance", icon: Target, module: "performance" },
    ],
  },
  {
    label: "Finance",
    items: [
      { to: "/payroll", label: "Payroll", icon: Wallet, module: "payroll" },
      { to: "/increments", label: "Increments", icon: TrendingUp, module: "increments" },
      { to: "/loans", label: "Loans", icon: PiggyBank, module: "loans" },
      { to: "/tada", label: "TA / DA", icon: Receipt, module: "tada" },
      { to: "/designations", label: "Depts & Designations", icon: BadgeIndianRupee, module: "designations" },
    ],
  },
  {
    label: "Fleet & Facilities",
    items: [
      { to: "/vehicles", label: "Company Vehicles", icon: Car, module: "vehicles" },
      { to: "/mobile-numbers", label: "Mobile Numbers", icon: Smartphone, module: "mobile_numbers" },
    ],
  },
  {
    label: "Lifecycle",
    items: [
      { to: "/recruitment", label: "Recruitment", icon: UserPlus, module: "recruitment" },
      { to: "/exits", label: "Exits", icon: UserMinus, module: "exits" },
      { to: "/assets", label: "Assets", icon: Smartphone, module: "assets" },
    ],
  },
  {
    label: "Insights",
    items: [
      { to: "/compliance", label: "Compliance", icon: FileCheck2, module: "compliance" },
      { to: "/reports", label: "Reports", icon: FileSpreadsheet, module: "reports" },
      { to: "/ai-summaries", label: "AI Summaries", icon: Sparkles, module: "reports" },
      { to: "/notifications", label: "Notifications", icon: Bell, module: "notifications" },
      { to: "/me", label: "My Profile", icon: Briefcase, module: "my_profile" },
    ],
  },
];

const ADMIN_ITEMS = [
  { to: "/users", label: "Users & Access", icon: UserCog },
  { to: "/admin", label: "Danger Zone", icon: ShieldAlert },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="flex min-h-screen" style={{ background: "var(--bg-page)" }}>
      {mobileOpen && <button className="fixed inset-0 z-40 bg-black/40 lg:hidden" onClick={() => setMobileOpen(false)} aria-label="Close navigation" data-testid="mobile-nav-backdrop" />}
      <aside className={`${mobileOpen ? "flex" : "hidden"} lg:flex fixed lg:sticky top-0 left-0 h-dvh z-50 lg:z-auto w-64 shrink-0 bg-white border-r border-border/60 flex-col`} data-testid="sidebar">
        <button className="absolute right-2 top-2 p-1 lg:hidden rounded hover:bg-stone-100 transition-colors" aria-label="Close navigation" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-close"><X className="w-4 h-4" /></button>
        <div className="px-6 py-6 border-b border-border/40 flex items-center gap-3">
          <div className="w-11 h-11 rounded-lg overflow-hidden flex items-center justify-center bg-black flex-shrink-0">
            <img src="/navaratnaa-logo.png" alt="Navaratnaa" className="w-full h-full object-contain" />
          </div>
          <div>
            <div className="font-heading font-bold text-base leading-tight" style={{ color: "var(--color-primary)" }}>
              Navaratnaa
            </div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-stone-500 font-semibold">HRMS</div>
          </div>
        </div>
        <nav className="flex-1 px-3 py-4 space-y-4 overflow-y-auto">
          {NAV_GROUPS.map((g) => {
            const visible = g.items.filter((n) => hasModule(user, n.module));
            if (visible.length === 0) return null;
            return (
              <div key={g.label}>
                <div className="kpi-label px-3 mb-1.5 text-stone-400" style={{ fontSize: 10 }}>{g.label}</div>
                <div className="space-y-1">
                  {visible.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.to === "/"}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`nav-${n.label.toLowerCase().replace(/[\s/]+/g, "-").replace(/-+/g, "-")}`}
                      className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}
                    >
                      <n.icon className="w-4 h-4 nav-icon" />
                      <span>{n.label}</span>
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
          {user?.role === "super_admin" && (
            <div>
              <div className="kpi-label px-3 mb-1.5" style={{ fontSize: 10, color: "#9A3412" }}>Admin</div>
              <div className="space-y-1">
                {ADMIN_ITEMS.map((n) => (
                  <NavLink key={n.to} to={n.to} onClick={() => setMobileOpen(false)}
                           data-testid={`nav-${n.label.toLowerCase().replace(/[\s/&]+/g, "-").replace(/-+/g, "-")}`}
                           className={({ isActive }) => `nav-link ${isActive ? "active" : ""}`}>
                    <n.icon className="w-4 h-4 nav-icon" />
                    <span>{n.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          )}
        </nav>
        <div className="p-3 border-t border-border/40">
          <div className="flex items-center gap-3 px-2 py-2">
            <div className="w-9 h-9 rounded-full flex items-center justify-center text-white text-sm font-semibold"
                 style={{ background: "var(--color-secondary)" }}>
              {user?.name?.[0] || "U"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold truncate" data-testid="user-name">{user?.name}</div>
              <div className="text-xs text-stone-500 truncate capitalize">{user?.role?.replace("_", " ")}</div>
            </div>
            <button onClick={handleLogout} data-testid="logout-button"
                    className="p-2 rounded-md text-stone-500 hover:bg-stone-100 hover:text-stone-900 transition-colors">
              <LogOut className="w-4 h-4" />
            </button>
          </div>
        </div>
      </aside>

      <main className="flex-1 min-w-0 flex flex-col">
        <header className="bg-white border-b border-border/40 px-4 sm:px-8 py-4 flex items-center gap-3">
          <button className="lg:hidden p-2 rounded-md hover:bg-stone-100 transition-colors shrink-0" onClick={() => setMobileOpen(!mobileOpen)} aria-expanded={mobileOpen} aria-label="Open navigation" data-testid="mobile-nav-open"><Menu className="w-5 h-5" /></button>
          <div className="flex items-center gap-2 flex-1 min-w-0 max-w-md bg-stone-100 rounded-md px-3 py-2">
            <Search className="w-4 h-4 text-stone-500" />
            <input
              data-testid="global-search-input"
              placeholder="Search employees, modules…"
              className="bg-transparent outline-none text-sm flex-1 min-w-0 placeholder:text-stone-500"
            />
          </div>
          <button className="p-2 rounded-md hover:bg-stone-100 transition-colors relative" data-testid="notifications-button">
            <Bell className="w-5 h-5 text-stone-600" />
            <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full" style={{ background: "var(--color-secondary)" }}></span>
          </button>
        </header>
        <div className="flex-1 min-w-0 p-4 sm:p-8 overflow-y-auto">{children}</div>
      </main>
    </div>
  );
}
